import { useState } from 'react';
import type { InventoryItem } from '../types/inventory.types';
import { N11Service } from '../services';

interface UseN11ProductSendProps {
  companyId: string | null;
  getBrandName: (brandId: string | null) => string;
  showSnackbar: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
  saveN11Log: (logEntry: {
    type: 'price-stock-update' | 'product-create' | 'product-save';
    productName: string;
    stockCode: string;
    taskId: number;
    status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT';
    operation: string;
    details: any;
  }) => void;
}

export const useN11ProductSend = ({
  companyId,
  getBrandName,
  showSnackbar,
  saveN11Log,
}: UseN11ProductSendProps) => {
  const [n11SendingProduct, setN11SendingProduct] = useState<boolean>(false);

  const sendProductToN11 = async (
    item: InventoryItem,
    categoryId: string,
    shipmentTemplate?: string
  ): Promise<boolean> => {
    if (!companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      return false;
    }

    // N11 zorunlu alanları kontrol et
    const validation = N11Service.validateN11Product(item, getBrandName);
    if (!validation.isValid) {
      const missingFieldsText = validation.missingFields.join(', ');
      showSnackbar(
        `Şu bilgiler eksik olduğu için ürün N11'e gönderilemez:\n\n${missingFieldsText}\n\nLütfen bu bilgileri tamamlayınız.\n\nÜrün: ${item.name}`,
        'error'
      );
      return false;
    }

    setN11SendingProduct(true);
    
    try {
      showSnackbar('Ürün N11\'e gönderiliyor...', 'info');
      
      const brandName = getBrandName(item.brandId || null);
      
      // N11Service kullanarak ürünü gönder
      const result = await N11Service.sendProductToN11(
        companyId,
        item,
        categoryId,
        brandName,
        shipmentTemplate
      );

      if (!result.success) {
        throw new Error(result.error || 'Ürün gönderme işlemi başarısız');
      }

      // Log kaydet
      const logData = N11Service.prepareN11LogData(
        'product-save',
        item.name,
        item.stockCode,
        result.taskId!,
        result.status as 'IN_QUEUE' | 'PROCESSED' | 'REJECT',
        `Mevcut ürün N11'e gönderildi - Kategori: ${categoryId}`,
        {
          categoryId,
          brandName,
          salePrice: item.salePrice,
          listPrice: (item as any).listPrice || item.salePrice,
          quantity: item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0,
          vatRate: item.vatRate || 18,
          shipmentTemplate: shipmentTemplate || 'BAREM',
          hasImages: !!item.imageUrl
        }
      );
      saveN11Log(logData);
      
      // Başarı mesajı
      const successMessage = N11Service.formatSuccessMessage(
        item.name,
        result.taskId!,
        result.status!,
        result.reasons
      );
      showSnackbar(successMessage, 'success');
      
      return true;
      
    } catch (error) {
      console.error('❌ [N11 SEND PRODUCT] Ürün gönderme hatası:', error);
      showSnackbar(error instanceof Error ? error.message : 'Ürün gönderme sırasında bir hata oluştu', 'error');
      return false;
    } finally {
      setN11SendingProduct(false);
    }
  };

  return {
    n11SendingProduct,
    sendProductToN11,
  };
};

